import { useCallback, useContext, useEffect } from "react";
import {
  useAnimatedStyle,
  useSharedValue,
  withTiming,
} from "react-native-reanimated";
import { Adapt, Sheet, View, useMedia } from "tamagui";

import { AnimatedView } from "./animated";
import {
  ANIM_DURATION,
  DevSidebarValue,
  LayoutContext,
} from "./LayoutProvider";

const RIGHT_SIDEBAR_WIDTH = 400;
const RIGHT_SIDEBAR_GAP = 16;

type DevSidebarProps = {
  onClose: () => void;
  visible: boolean;
  Content: DevSidebarValue;
};

const Modal = ({ visible, onClose, Content }: DevSidebarProps) => {
  return (
    <Adapt when="maxLg">
      <Sheet
        modal
        snapPoints={[98]}
        open={visible}
        dismissOnOverlayPress={false}
        disableDrag
        onOpenChange={(open: boolean) => {
          if (!open) {
            onClose();
          }
        }}
      >
        <Sheet.Overlay
          transition="standard"
          backgroundColor="$fillOverlay"
          enterStyle={{ opacity: 0 }}
          exitStyle={{ opacity: 0 }}
        />
        <Sheet.Frame backgroundColor="$fillModal">
          {!!Content && <Content onClose={onClose} />}
        </Sheet.Frame>
      </Sheet>
    </Adapt>
  );
};

const RightSidebar = ({ visible, onClose, Content }: DevSidebarProps) => {
  const width = useSharedValue(0);
  const leftMargin = useSharedValue(0);

  const animatedStyle = useAnimatedStyle(
    () => ({ width: width.value, marginLeft: leftMargin.value }),
    [width, leftMargin],
  );

  useEffect(() => {
    width.value = withTiming(visible ? RIGHT_SIDEBAR_WIDTH : 0, {
      duration: ANIM_DURATION,
    });
    leftMargin.value = withTiming(visible ? RIGHT_SIDEBAR_GAP : 0, {
      duration: ANIM_DURATION,
    });
  }, [width, visible, leftMargin]);

  return (
    <AnimatedView height="100%" overflow="hidden" style={animatedStyle}>
      {!!Content && (
        <View
          position="absolute"
          top={0}
          right={0}
          height="100%"
          width={RIGHT_SIDEBAR_WIDTH}
        >
          <Content onClose={onClose} />
        </View>
      )}
    </AnimatedView>
  );
};

export default function DevSidebar() {
  const { lg } = useMedia();

  const { DevSidebarContent, devSidebarOpen, setDevSidebarContent } =
    useContext(LayoutContext);

  const onClose = useCallback(() => {
    setDevSidebarContent(null);
  }, [setDevSidebarContent]);

  const props = {
    visible: devSidebarOpen,
    onClose,
    Content: DevSidebarContent,
  };

  return (
    <>
      <Modal {...props} />
      {lg && <RightSidebar {...props} />}
    </>
  );
}
